import React from "react";

/**
 * Fără un error boundary, orice excepție aruncată la render demontează tot
 * arborele React și pagina rămâne complet albă, fără nicio urmă a erorii.
 *
 * O parte din erori sunt tranzitorii: dacă ceva din exteriorul React-ului
 * modifică DOM-ul (traducerea automată a paginii, o extensie de browser), React
 * nu își mai găsește nodurile și aruncă la următoarea actualizare. O remontare
 * curată rezolvă cazul, așa că încercăm asta întâi și arătăm mesajul de eroare
 * doar dacă problema persistă.
 */
const AUTO_RECOVER_LIMIT = 1;

export default class ErrorBoundary extends React.Component {
   constructor(props) {
      super(props);
      this.state = { error: null, info: null, remountKey: 0 };
      this.recoveryAttempts = 0;
      this.handleRetry = this.handleRetry.bind(this);
      this.handleReload = this.handleReload.bind(this);
   }

   static getDerivedStateFromError(error) {
      return { error };
   }

   componentDidCatch(error, info) {
      // eslint-disable-next-line no-console
      console.error(
         `[ErrorBoundary${this.props.name ? ` ${this.props.name}` : ""}]`,
         error,
         info?.componentStack,
      );
      this.props.onError?.(error, info);

      if (this.recoveryAttempts < AUTO_RECOVER_LIMIT) {
         this.recoveryAttempts += 1;
         this.setState((prev) => ({
            error: null,
            info: null,
            remountKey: prev.remountKey + 1,
         }));
         return;
      }

      this.setState({ info });
   }

   componentDidUpdate(prevProps) {
      if (prevProps.resetKey === this.props.resetKey) return;
      this.recoveryAttempts = 0;
      if (this.state.error) this.setState({ error: null, info: null });
   }

   handleRetry() {
      this.recoveryAttempts = 0;
      this.setState((prev) => ({
         error: null,
         info: null,
         remountKey: prev.remountKey + 1,
      }));
   }

   handleReload() {
      window.location.reload();
   }

   render() {
      const { error, info, remountKey } = this.state;

      if (!error) {
         return (
            <React.Fragment key={remountKey}>
               {this.props.children}
            </React.Fragment>
         );
      }

      const message = String(error?.message || error || "Eroare necunoscută");

      return (
         <div className="error-boundary" role="alert">
            <div className="error-boundary__body">
               <h2 className="error-boundary__title">
                  {this.props.title || "Ceva s-a rupt în această pagină"}
               </h2>
               <p className="error-boundary__text">
                  Restul aplicației funcționează. Încearcă din nou, iar dacă
                  eroarea revine trimite textul de mai jos echipei tehnice.
               </p>
               <pre className="error-boundary__details">
                  {message}
                  {info?.componentStack ? `\n${info.componentStack}` : ""}
               </pre>
               <div className="error-boundary__actions">
                  <button
                     type="button"
                     className="error-boundary__btn error-boundary__btn--primary"
                     onClick={this.handleRetry}
                  >
                     Încearcă din nou
                  </button>
                  <button
                     type="button"
                     className="error-boundary__btn"
                     onClick={this.handleReload}
                  >
                     Reîncarcă pagina
                  </button>
               </div>
            </div>
         </div>
      );
   }
}
